import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static values = {
    userId: String,
    userName: String,
    shape: { type: String, default: 'auto' }
  }

  connect () {
    this.store = window.__profilePreviewStore ||= {}
    // Prevent the next user's profile dropdown from opening after clicking inside the current dropdown
    this.state = window.__profilePreviewState ||= {
      hoverSuppressedUntil: 0
    }

    if (!this.state.modalListenerBound) {
      document.addEventListener('show.bs.modal', () => {
        this.state.modalOpen = true
        Object.values(window.__profilePreviewStore || {}).forEach(entry => {
          entry?.activeController?.hide()
        })
      })
      document.addEventListener('hidden.bs.modal', () => {
        this.state.modalOpen = Boolean(document.querySelector('.modal.show'))
      })
      this.state.modalListenerBound = true
    }

    if (!this.state.beforeCacheListenerBound) {
      document.addEventListener('turbo:before-cache', () => {
        Object.values(window.__profilePreviewStore || {}).forEach(entry => {
          if (!entry?.node) return

          entry.node.style.display = 'none'
          entry.activeController = null
        })
      })
      this.state.beforeCacheListenerBound = true
    }

    this.ensureSharedDropdown()

    this._isMouseInside = false
    this._onMouseEnter = this.onMouseEnter.bind(this)
    this._onMouseMove = this.onMouseMove.bind(this)
    this._onMouseLeave = this.onMouseLeave.bind(this)
    this._outsideClick = this.handleOutsideClick.bind(this)
    this._insideClick = this.clickShow.bind(this)

    this.element.addEventListener('mouseenter', this._onMouseEnter)
    this.element.addEventListener('mousemove', this._onMouseMove)
    this.element.addEventListener('mouseleave', this._onMouseLeave)

    if (!this.entry.eventsBound) {
      this._dropdownEnter = this.onDropdownEnter.bind(this)
      this._dropdownLeave = this.onDropdownLeave.bind(this)
      this._dropdownClick = this.onDropdownClick.bind(this)

      this.dropdown.addEventListener('mouseenter', this._dropdownEnter)
      this.dropdown.addEventListener('mouseleave', this._dropdownLeave)
      this.dropdown.addEventListener('click', this._dropdownClick)

      this.entry.eventsBound = true
    }

    if (!this.entry.loaded && !this.entry.loading) {
      this.loadUserDataOnce()
    }

    this._onScroll = this.handleScroll.bind(this)
    window.addEventListener('scroll', this._onScroll, true)
    window.addEventListener('resize', this._onScroll)
  }

  handleScroll () {
    Object.values(this.store).forEach(entry => {
      if (!entry?.node) return

      entry.node.style.display = 'none'

      if (entry.activeController) {
        entry.activeController.entry.activeController = null
      }
    })
  }

  disconnect () {
    this.clearHideTimer()

    if (this.entry?.activeController === this) {
      this.hide()
    }

    this.element.removeEventListener('mouseenter', this._onMouseEnter)
    this.element.removeEventListener('mousemove', this._onMouseMove)
    this.element.removeEventListener('mouseleave', this._onMouseLeave)

    if (
      this.entry &&
      this.entry.node &&
      !document.body.contains(this.entry.node)
    ) {
      delete this.store[this.userIdValue]
    }
    window.removeEventListener('scroll', this._onScroll, true)
    window.removeEventListener('resize', this._onScroll)
  }

  get isCircular () {
    if (this.shapeValue === 'circle') return true
    if (this.shapeValue === 'rect') return false

    return (
      this.element.matches('[class*="user-profile-"]') ||
      this.element.classList.contains('rounded-circle') ||
      Boolean(this.element.querySelector('.main-user-img, .rounded-circle, .dp-image, svg'))
    )
  }

  isInsideTarget (e) {
    if (!this.isCircular) return true
    if (!e || typeof e.clientX !== 'number' || typeof e.clientY !== 'number') return true

    const rect = this.element.getBoundingClientRect()
    if (!rect.width || !rect.height) return true

    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2
    const radius = Math.min(rect.width, rect.height) / 2

    const dx = e.clientX - centerX
    const dy = e.clientY - centerY

    return (dx * dx + dy * dy) <= (radius * radius)
  }

  isElementHovered () {
    if (!this.element.matches(':hover')) return false
    if (this.isCircular) {
      return this._isMouseInside === true
    }
    return true
  }

  onMouseEnter (e) {
    if (!this.isInsideTarget(e)) {
      this._isMouseInside = false
      return
    }

    this._isMouseInside = true
    this.show()
  }

  onMouseMove (e) {
    const inside = this.isInsideTarget(e)

    if (inside) {
      if (!this._isMouseInside) {
        this._isMouseInside = true
        this.clearHideTimer()
        this.show()
      } else {
        this.clearHideTimer()
      }
    } else {
      if (this._isMouseInside) {
        this._isMouseInside = false
        this.scheduleHide()
      }
    }
  }

  onMouseLeave (e) {
    this._isMouseInside = false
    this.hideIfOutside(e)
  }

  ensureSharedDropdown () {
    const key = this.userIdValue
    const existing = this.store[key]

    if (existing) {
      if (document.body.contains(existing.node)) {
        this.entry = existing
        this.dropdown = existing.node
        return
      }

      delete this.store[key]
    }

    const dropdown = document.createElement('div')

    dropdown.className =
      'dropdown-menu show bg-img-dot rounded-4 text-start bg-sky-blue border-3 border-solid border-black bg-repeat bg-auto py-10 px-10 px-md-20 p-2 text-black w-230px'

    dropdown.style.position = 'fixed'
    dropdown.style.display = 'none'
    dropdown.style.zIndex = '9999'

    dropdown.innerHTML = `
      <div class="p-3">
        <div class="placeholder-glow">
          <span class="placeholder col-12"></span>
          <span class="placeholder col-8"></span>
          <span class="placeholder col-6"></span>
        </div>
      </div>
    `

    document.body.appendChild(dropdown)

    this.store[key] = {
      node: dropdown,
      loaded: false,
      loading: false,
      activeController: null,
      eventsBound: false
    }

    this.entry = this.store[key]
    this.dropdown = dropdown
  }

  async loadUserDataOnce () {
    if (this.entry.loading || this.entry.loaded) return

    this.entry.loading = true

    try {
      const response = await fetch(
        `/profiles/${this.userNameValue}/user_hover_card`,
        {
          headers: {
            Accept: 'text/html'
          }
        }
      )

      const html = await response.text()

      this.dropdown.innerHTML = html
      this.entry.loaded = true
    } catch (e) {
      this.dropdown.innerHTML =
        "<div class='p-3'>Failed to load profile</div>"
    } finally {
      this.entry.loading = false
    }
  }

  show () {
    const activeModal = document.querySelector('.modal.show')
    const triggerModal = this.element.closest('.modal.show')

    if ((this.state.modalOpen || activeModal) && !triggerModal) {
      return
    }

    if (Date.now() < this.state.hoverSuppressedUntil) {
      return
    }

    this.clearHideTimer()

    Object.values(this.store).forEach(entry => {
      if (
        entry?.activeController &&
        entry.activeController !== this
      ) {
        entry.activeController.hide()
      }
    })

    if (!document.body.contains(this.dropdown)) {
      delete this.store[this.userIdValue]
      this.ensureSharedDropdown()
    }

    this.entry.activeController = this

    this.position()
    this.dropdown.style.display = 'block'

    if (!this.entry.loaded && !this.entry.loading) {
      this.loadUserDataOnce()
    }
  }

  hide () {
    this.dropdown.style.display = 'none'

    if (this.entry.activeController === this) {
      this.entry.activeController = null
    }
  }

  position () {
    const rect = this.element.getBoundingClientRect()

    this.dropdown.style.visibility = 'hidden'
    this.dropdown.style.display = 'block'

    const dropdownRect = this.dropdown.getBoundingClientRect()
    const viewportHeight = window.innerHeight
    const viewportWidth = window.innerWidth
    const gap = 0

    const spaceBelow = viewportHeight - rect.bottom
    const spaceAbove = rect.top

    let top

    if (spaceBelow >= dropdownRect.height) {
      top = rect.bottom + gap
    } else if (spaceAbove >= dropdownRect.height) {
      top = rect.top - dropdownRect.height - gap
    } else {
      top = spaceBelow > spaceAbove
        ? rect.bottom + gap
        : Math.max(10, rect.top - dropdownRect.height - gap)
    }

    let left = rect.left

    if (left + dropdownRect.width > viewportWidth) {
      left = viewportWidth - dropdownRect.width - 10
    }

    if (left < 10) {
      left = 10
    }

    this.dropdown.style.left = `${left}px`
    this.dropdown.style.top = `${top}px`
    this.dropdown.style.visibility = 'visible'
  }

  hideIfOutside (e) {
    const related = e.relatedTarget

    if (
      related &&
      (
        this.element.contains(related) ||
        this.dropdown.contains(related)
      )
    ) {
      return
    }

    this.scheduleHide()
  }

  onDropdownEnter () {
    this.clearHideTimer()
  }

  onDropdownLeave (e) {
    const related = e.relatedTarget

    if (
      related &&
      (
        !this.element.contains(related) &&
        !this.dropdown.contains(related)
      )
    ) {
      this.hide()
    }
  }

  onDropdownClick (e) {
    const related = e.target

    if (related && this.dropdown.contains(related)) {
      this.state.hoverSuppressedUntil = Date.now() + 300
      this.hide()
    }
  }

  scheduleHide () {
    this.clearHideTimer()

    this.hideTimer = setTimeout(() => {
      if (this.entry.activeController !== this) return

      const dropdownHovered = this.dropdown && this.dropdown.matches(':hover')
      if (!this.isElementHovered() && !dropdownHovered) {
        this.hide()
      }
    }, 200)
  }

  clearHideTimer () {
    if (this.hideTimer) {
      clearTimeout(this.hideTimer)
      this.hideTimer = null
    }
  }

  clickShow (e) {
    e.preventDefault()
    e.stopPropagation()

    if (
      this.dropdown.style.display === 'block' &&
      this.entry.activeController === this
    ) {
      this.hide()
    } else {
      this.show()
    }
  }

  handleOutsideClick (e) {
    const target = e.target

    if (
      !this.element.contains(target) &&
      !this.dropdown.contains(target)
    ) {
      this.hide()
    }
  }

  isTouchDevice () {
    return (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0
    )
  }
}
